/**
 * Telegram Mini App SDK hook.
 *
 * Wraps the global window.Telegram.WebApp object behind a clean React
 * interface. Provides:
 *   - initData: the raw signed string we send to backend for JWT exchange
 *   - user: parsed Telegram user object
 *   - haptic: helpers for vibration feedback (notification + impact)
 *   - showBackButton / hideBackButton
 *   - close: dismiss the mini app
 *
 * Luna Protokol is **dark-only** — we ignore Telegram's colorScheme and
 * always render on a deep cosmic background. This keeps the brand look
 * consistent regardless of what theme the user has in Telegram.
 *
 * Falls back to safe defaults when running in a regular browser
 * (e.g. local dev) so the UI still renders.
 */
import { useEffect, useCallback } from 'react';

interface TelegramUser {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  language_code?: string;
  photo_url?: string;
}

interface TelegramWebAppApi {
  initData: string;
  initDataUnsafe: { user?: TelegramUser };
  colorScheme: 'light' | 'dark';
  themeParams: Record<string, string>;
  isExpanded: boolean;
  viewportHeight: number;
  version?: string;
  expand: () => void;
  close: () => void;
  ready: () => void;
  /** Telegram WebApp 7.7+ — locks the mini app from being dragged
   *  down to dismiss when the user pulls from the top of the page. */
  disableVerticalSwipes?: () => void;
  enableVerticalSwipes?: () => void;
  HapticFeedback?: {
    impactOccurred: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void;
    notificationOccurred: (type: 'error' | 'success' | 'warning') => void;
    selectionChanged: () => void;
  };
  BackButton?: {
    show: () => void;
    hide: () => void;
    onClick: (cb: () => void) => void;
    offClick: (cb: () => void) => void;
  };
  MainButton?: {
    setText: (text: string) => void;
    show: () => void;
    hide: () => void;
    onClick: (cb: () => void) => void;
    offClick: (cb: () => void) => void;
  };
  setHeaderColor?: (color: string) => void;
  setBackgroundColor?: (color: string) => void;
}

declare global {
  interface Window {
    Telegram?: {
      WebApp?: TelegramWebAppApi;
    };
  }
}

export interface UseTelegramWebApp {
  initData: string;
  user: TelegramUser | null;
  isAvailable: boolean;
  haptic: {
    light: () => void;
    medium: () => void;
    heavy: () => void;
    success: () => void;
    error: () => void;
    warning: () => void;
    selection: () => void;
  };
  setupBackButton: (handler: () => void) => () => void;
  close: () => void;
}

const noop = (): void => undefined;

export function useTelegramWebApp(): UseTelegramWebApp {
  const tg = typeof window !== 'undefined' ? window.Telegram?.WebApp : undefined;
  const isAvailable = !!tg;

  useEffect(() => {
    // Luna is dark-only — always enforce the dark class on <html>, no
    // matter what Telegram's colorScheme says. This avoids any chance
    // of a flash-of-light-theme on slow devices.
    document.documentElement.classList.add('dark');
    document.documentElement.classList.remove('light');

    if (!tg) return;
    tg.ready();
    tg.expand();
    // Lock the mini app so a downward pull from the top of the page
    // doesn't try to dismiss it (Telegram WebApp 7.7+).
    tg.disableVerticalSwipes?.();
    // Force the Telegram chrome to our dark background colour so the
    // header/status bar don't flash the default Telegram accent.
    tg.setHeaderColor?.('#07070d');
    tg.setBackgroundColor?.('#07070d');
  }, [tg]);

  const haptic = {
    light: useCallback(() => tg?.HapticFeedback?.impactOccurred('light') ?? noop(), [tg]),
    medium: useCallback(() => tg?.HapticFeedback?.impactOccurred('medium') ?? noop(), [tg]),
    heavy: useCallback(() => tg?.HapticFeedback?.impactOccurred('heavy') ?? noop(), [tg]),
    success: useCallback(
      () => tg?.HapticFeedback?.notificationOccurred('success') ?? noop(),
      [tg],
    ),
    error: useCallback(
      () => tg?.HapticFeedback?.notificationOccurred('error') ?? noop(),
      [tg],
    ),
    warning: useCallback(
      () => tg?.HapticFeedback?.notificationOccurred('warning') ?? noop(),
      [tg],
    ),
    selection: useCallback(() => tg?.HapticFeedback?.selectionChanged() ?? noop(), [tg]),
  };

  const setupBackButton = useCallback(
    (handler: () => void) => {
      if (!tg?.BackButton) return noop;
      tg.BackButton.show();
      tg.BackButton.onClick(handler);
      return () => {
        tg.BackButton?.offClick(handler);
        tg.BackButton?.hide();
      };
    },
    [tg],
  );

  return {
    initData: tg?.initData ?? '',
    user: tg?.initDataUnsafe?.user ?? null,
    isAvailable,
    haptic,
    setupBackButton,
    close: () => tg?.close(),
  };
}
